import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NAV_LINKS } from '../data/mock';
import { useAuth } from '../context/AuthContext';

const CrossLogo = ({ small = false }) => (
  <svg
    viewBox="0 0 32 48"
    xmlns="http://www.w3.org/2000/svg"
    className={small ? 'h-6 w-4' : 'h-8 w-5 md:h-10 md:w-6'}
    aria-hidden="true"
  >
    <path
      d="M13 0 h6 v16 h13 v6 h-13 v26 h-6 v-26 h-13 v-6 h13 z"
      fill="#3a2359"
    />
  </svg>
);

/**
 * Desktop nav item — supports optional `children` dropdown on hover.
 * `scrolled` swells the text/tap-target when the logo row is collapsed.
 */
const DesktopNavItem = ({ link, isActive, scrolled }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const hasChildren = Array.isArray(link.children) && link.children.length > 0;

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  // Small close delay so the mouse can travel from trigger to menu without gap issues
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);

  // Bigger text when scrolled so the nav bar stays easy to spot and click
  const sizeClass = scrolled ? 'text-[17px]' : 'text-[15px]';

  if (!hasChildren) {
    return (
      <NavLink
        to={link.path}
        end={link.path === '/'}
        className={({ isActive: nActive }) =>
          `${sizeClass} tracking-wide font-medium text-[#1c1917] hover:text-[#3a2359] relative after:content-[''] after:absolute after:-bottom-1 after:left-0 after:h-[1.5px] after:bg-[#3a2359] after:transition-all after:duration-300 ${nActive ? 'text-[#3a2359] after:w-full' : 'after:w-0 hover:after:w-full'}`
        }
      >
        {link.label}
      </NavLink>
    );
  }

  return (
    <div className="relative" onMouseEnter={openNow} onMouseLeave={scheduleClose}>
      <NavLink
        to={link.path}
        className={({ isActive: nActive }) =>
          `flex items-center gap-1 ${sizeClass} tracking-wide font-medium text-[#1c1917] hover:text-[#3a2359] relative pb-1 after:content-[''] after:absolute after:-bottom-0 after:left-0 after:h-[1.5px] after:bg-[#3a2359] after:transition-all after:duration-300 ${(nActive || isActive) ? 'text-[#3a2359] after:w-full' : 'after:w-0 hover:after:w-full'}`
        }
      >
        {link.label}
        <ChevronDown size={scrolled ? 14 : 13} className={`transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={2} />
      </NavLink>

      {/* Dropdown */}
      <div
        className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 min-w-[240px] z-50 ${open ? 'block' : 'hidden'}`}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
      >
        <div className="bg-white border border-[#e9e0cc] shadow-lg py-2">
          {link.children.map((child) => (
            <NavLink
              key={child.path}
              to={child.path}
              end
              className={({ isActive: cActive }) =>
                `block px-5 py-2.5 text-sm ${cActive ? 'bg-[#f2ead6]/60 text-[#3a2359] font-medium' : 'text-[#2a1a45] hover:bg-[#faf7f0] hover:text-[#3a2359]'}`
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState(null);
  const location = useLocation();
  const { isAdmin } = useAuth();

  useEffect(() => {
    // rAF-throttled scroll listener with hysteresis to prevent flicker
    // at the boundary. Cross the threshold once at 24 px and only reset
    // below 8 px — avoids rapid toggling on trackpad micro-scrolls.
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(prev => {
          if (prev && y < 8) return false;
          if (!prev && y > 24) return true;
          return prev;
        });
        ticking = false;
      });
    };
    // Initial sync
    setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setExpandedMobile(null);
  }, [location.pathname]);

  // Determine if a section is active based on the URL prefix
  const isSectionActive = (link) => {
    if (link.path === '/') return location.pathname === '/';
    return location.pathname === link.path || location.pathname.startsWith(link.path + '/');
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky ${isAdmin ? 'top-[34px]' : 'top-0'} z-50 bg-[#faf7f0] border-b transition-[box-shadow,border-color] duration-300 ease-out ${
        scrolled ? 'shadow-sm border-[#e9e0cc]' : 'border-transparent'
      }`}
      style={{ willChange: 'box-shadow, border-color', WebkitBackfaceVisibility: 'hidden', transform: 'translateZ(0)' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Logo Row — collapses on desktop when scrolled (mobile keeps hamburger visible).
            Mobile padding stays constant to avoid any layout shift. Only desktop animates. */}
        <div
          data-testid="header-logo-row"
          className={`flex items-center justify-center relative overflow-hidden py-3 transition-[max-height,opacity,padding-top,padding-bottom] duration-300 ease-out ${
            scrolled
              ? 'max-h-[80px] md:max-h-0 md:py-0 md:opacity-0 md:pointer-events-none'
              : 'max-h-[140px] md:py-4 opacity-100'
          }`}
        >
          <Link to="/" className="flex items-center gap-3 md:gap-4 group pr-14 md:pr-0 max-w-full">
            <CrossLogo />
            <div className="flex flex-col items-start min-w-0">
              <span className="font-display text-xl sm:text-2xl md:text-4xl text-[#2a1a45] font-medium tracking-tight leading-none truncate">
                All Saints Catholic Parish
              </span>
              <span className="mt-1 md:mt-1.5 text-[10px] sm:text-[11px] md:text-xs tracking-[0.18em] uppercase text-[#7a6f5f]">
                Ladysmith &amp; Chemainus, Vancouver Island
              </span>
            </div>
          </Link>
          <button
            className="md:hidden absolute right-4 top-1/2 -translate-y-1/2 p-2 text-[#2a1a45] bg-[#faf7f0]"
            onClick={() => setMobileOpen(v => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Desktop Nav — grows more prominent when scrolled so buttons stay easy to spot and click. */}
        <nav className={`hidden md:flex items-center justify-center gap-8 lg:gap-12 relative transition-[padding-top,padding-bottom] duration-300 ease-out ${scrolled ? 'py-5' : 'pt-0 pb-4'}`}>
          {/* Compact brand shown only when scrolled so parishioners always know where they are */}
          <Link
            to="/"
            aria-hidden={!scrolled}
            tabIndex={scrolled ? 0 : -1}
            data-testid="header-compact-brand"
            className={`absolute left-0 top-1/2 -translate-y-1/2 flex items-center gap-2.5 text-[#2a1a45] hover:text-[#3a2359] transition-opacity duration-300 ease-out ${scrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <CrossLogo small />
            <span className="font-display text-xl leading-none">All Saints</span>
          </Link>
          {NAV_LINKS.map(link => (
            <DesktopNavItem key={link.path + link.label} link={link} isActive={isSectionActive(link)} scrolled={scrolled} />
          ))}
        </nav>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#e9e0cc] bg-[#faf7f0]">
          <nav className="flex flex-col px-6 py-2">
            {NAV_LINKS.map(link => {
              const hasChildren = Array.isArray(link.children) && link.children.length > 0;
              if (!hasChildren) {
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    end={link.path === '/'}
                    className={({ isActive }) =>
                      `py-3 border-b border-[#e9e0cc] text-base font-medium ${isActive ? 'text-[#3a2359]' : 'text-[#1c1917]'}`
                    }
                  >
                    {link.label}
                  </NavLink>
                );
              }
              const expanded = expandedMobile === link.label;
              return (
                <div key={link.path + link.label} className="border-b border-[#e9e0cc]">
                  <button
                    type="button"
                    onClick={() => setExpandedMobile(expanded ? null : link.label)}
                    className={`w-full py-3 flex items-center justify-between text-base font-medium ${isSectionActive(link) ? 'text-[#3a2359]' : 'text-[#1c1917]'}`}
                  >
                    <span>{link.label}</span>
                    <ChevronDown size={16} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded && (
                    <div className="pb-3 pl-4">
                      {link.children.map(child => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          end
                          className={({ isActive: cActive }) =>
                            `block py-2 text-sm ${cActive ? 'text-[#3a2359] font-medium' : 'text-[#5a534a]'}`
                          }
                        >
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
